import { Input } from '@chakra-ui/react'
import { useEffect, useState } from 'react'

import { FormField } from '../../../../components/FormField'
import { FormModal } from '../../../../components/FormModal'
import { FormSelect } from '../../../../components/Select'
import { useAppToast } from '../../../../helpers'
import UrlShortenerService, { IUtm } from '../../../../services/UrlShortener/UrlShortenerService'

interface IUtmListFormModalProps {
  isOpen: boolean
  onClose: () => void
  onSaved: () => void
  used_by: 'commercial' | 'marketing'
  editing?: IUtm | null
}

const UTM_TYPES: { value: IUtm['utm']; label: string }[] = [
  { value: 'campaign', label: 'UTM Campaign' },
  { value: 'source', label: 'UTM Source' },
  { value: 'medium', label: 'UTM Medium' },
  { value: 'content', label: 'UTM Content' },
  { value: 'term', label: 'UTM Term' }
]

export default function UtmListFormModal({ isOpen, onClose, onSaved, used_by, editing }: IUtmListFormModalProps) {
  const [utm, setUtm] = useState<IUtm['utm']>('campaign')
  const [value, setValue] = useState('')
  const [loading, setLoading] = useState(false)
  const [valueError, setValueError] = useState('')
  const toast = useAppToast()

  useEffect(() => {
    setUtm(editing?.utm ?? 'campaign')
    setValue(editing?.value ?? '')
    setValueError('')
  }, [isOpen, editing])

  const handleClose = () => {
    setUtm('campaign')
    setValue('')
    setValueError('')
    onClose()
  }

  const handleSubmit = async () => {
    if (!value.trim()) {
      setValueError('O valor é obrigatório.')
      return
    }

    setLoading(true)
    setValueError('')

    try {
      if (editing) {
        const { affected_urls } = await UrlShortenerService.updateUtm(editing.id, value.trim())
        toast.success(
          affected_urls > 0
            ? `UTM atualizada com sucesso em ${affected_urls} URL(s) curta(s)`
            : 'UTM atualizada com sucesso'
        )
      } else {
        await UrlShortenerService.createUtm(value.trim(), utm, used_by)
        toast.success('UTM criada com sucesso')
      }
      handleClose()
      onSaved()
    } catch (e) {
      const err = e as { response?: { status?: number; data?: { message?: string } } }
      const status = err?.response?.status
      const message = err?.response?.data?.message

      if (status === 422 && message === 'UTM já cadastrada.') {
        setValueError('UTM já cadastrada para este tipo e contexto.')
      } else {
        toast.error(editing ? 'Erro ao atualizar UTM' : 'Erro ao criar UTM', e)
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <FormModal
      isOpen={isOpen}
      onClose={handleClose}
      title={editing ? 'Editar UTM' : 'Nova UTM'}
      onSubmit={handleSubmit}
      size="compact"
      confirmLabel={editing ? 'Salvar' : 'Criar UTM'}
      isLoading={loading}
    >
      <FormField label="Tipo de UTM" isRequired>
        <FormSelect
          value={utm}
          onChange={(value) => setUtm(value as IUtm['utm'])}
          options={UTM_TYPES.map((t) => ({ value: t.value, label: t.label }))}
          hideEmptyOption
          // Só o valor é editável: trocar o tipo moveria a UTM de coluna nas URLs que a usam.
          isDisabled={!!editing}
        />
      </FormField>

      <FormField
        label="Valor"
        error={valueError}
        isRequired
        helperText={
          editing && editing.usage_count > 0
            ? `Usada em ${editing.usage_count} URL(s) curta(s) — o valor será atualizado nelas também.`
            : undefined
        }
      >
        <Input
          placeholder="Ex: campanha-junho-2026"
          value={value}
          onChange={(e) => {
            setValue(e.target.value)
            setValueError('')
          }}
        />
      </FormField>
    </FormModal>
  )
}
